'use client';

import { Button } from '@/components/ui/buttons/Button';
import { useTranslation } from '@/features/i18n';
import { Input } from '@/ui/form/Input';
import { Label } from '@/ui/form/Label';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuthentication } from '@vimond/features/authentication';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const SignUpConfirmCodeForm = ({
  email,
  logIn,
}: {
  email: string;
  logIn: (email?: string, password?: string) => void;
}) => {
  const { t } = useTranslation();

  const { confirmSignUp, resendSignUpCode } = useAuthentication();
  const [errorMessage, setErrorMessage] = useState<string>();
  const FormSchema = z.object({
    confirmationCode: z.string(),
  });

  type FormSchemaType = z.infer<typeof FormSchema>;
  const {
    formState: { errors, isDirty, isLoading, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    values: {
      confirmationCode: '',
    },
  });

  const onSubmit: SubmitHandler<FormSchemaType> = async (data) => {
    const { confirmationCode } = data;
    await confirmSignUp({ email, confirmationCode })
      .then((res) => {
        if (res.isSignUpComplete) {
          logIn();
          // window.location.href = '/auth/signin';
        }
      })
      .catch(() => {
        setErrorMessage(t('auth.invalid-confirmation-code-or-email'));
      });
  };

  return (
    <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-between">
            <Label htmlFor="confirmationCode">{t('auth.code')}</Label>
          </div>
          <div className="text-sm">
            <button
              type="button"
              disabled={email?.length === 0}
              onClick={async () =>
                await resendSignUpCode(email)
                  .then(() => {
                    setErrorMessage(t('auth.code-resent'));
                  })
                  .catch(() => {
                    setErrorMessage(t('auth.failed-to-resend-code'));
                  })
              }
              className="text-text-highlight cursor-pointer font-semibold disabled:opacity-50"
            >
              {t('auth.resend-code')}
            </button>
          </div>
        </div>
        <div className="mt-2">
          <Input
            id="confirmationCode"
            type="text"
            autoComplete="current-code"
            {...register('confirmationCode')}
            placeholder={t('auth.check-your-email-for-the-code')}
            aria-describedby="confirmationCode-error"
          />
        </div>
        <p
          id="confirmationCode-error"
          className="text-red-500"
          aria-live="polite"
          aria-atomic="true"
        >
          {errors.confirmationCode?.message}
        </p>
      </div>

      <Button
        variant="primary"
        size="sm"
        disabled={isLoading || isSubmitting || !isDirty}
        type="submit"
        className="w-full"
      >
        {t('auth.confirm-your-account')}
      </Button>

      {errorMessage && <p className="text-red-500">{errorMessage}</p>}
    </form>
  );
};
